/**
 * Shadow Breaker Pro — single-target, filtered port of MT4 ShadowBreaker_Pro_V2.mq4
 *
 * Shares the same two-stage signal engine as "Shadow Breaker":
 *   1. Nadaraya-Watson kernel regression envelope (causal Gaussian-weighted
 *      moving average ± mean-absolute-error × multiplier)
 *      - Price touching/crossing the lower band ARMS a BUY
 *      - Price touching/crossing the upper band ARMS a SELL
 *   2. SuperTrend (ATR based) flip TRIGGERS the armed entry
 *      - ND lower-band touch (armed) + ST flip UP   → Entry BUY
 *      - ND upper-band touch (armed) + ST flip DOWN  → Entry SELL
 *
 * What makes "Pro" different from the base Shadow Breaker:
 *   - A SINGLE target (not three). Touching it closes the whole trade —
 *     there is no partial-exit ladder.
 *   - A "thin candle" entry filter: an entry signal is rejected when the
 *     trigger candle's own High-Low range is too small relative to
 *     ATR(14) (< 35%) to trust as a genuine breakout bar — this is the
 *     small, indecisive candle that can sit right at a V-reversal and
 *     produce an unreliable signal.
 *   - Reversal ("flip") tagging: an entry that immediately follows an
 *     SL/TSL stop-out in the opposite direction (no flat period between)
 *     is flagged so the dashboard can call it out as a reversal re-entry.
 *
 * TP/SL (risk = |Entry − (SuperTrend line ∓ ATR(14) × 0.5 buffer)|):
 *   TP  = Entry ± 0.512 × risk
 *   SL  = Entry ∓ 0.896 × risk
 *   TSL = SuperTrend line ∓ ATR(14) × 0.5 — ratchets only in the trade's
 *         favour and becomes the effective stop once tighter than the fixed SL.
 *
 * Candle Colors: Green while a BUY is armed/active, Maroon while a SELL is
 * armed/active, Gray (neutral) after an SL/TSL exit until the next signal —
 * matches MT4's NeutralCandleAfterExit behaviour (a Target hit does NOT
 * reset color).
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;
type ExitKind = "" | "sl" | "tsl";

// ─── Strategy constants (MT4 ShadowBreaker_Pro_V2.mq4 Manual/Default inputs) ───
const ND_BANDWIDTH = 10;
const ND_MULTIPLIER = 2.0;
const ST_PERIOD = 14;
const ST_MULTIPLIER = 5.0;
const TP_RR = 0.512;
const SL_RR = 0.896;
const TSL_ATR_BUFFER = 0.5;
// "Thin candle" entry filter — rejects an entry whose trigger candle's own
// High-Low range is smaller than this fraction of half-ATR(14) (atrBufFor).
const THIN_CANDLE_MIN_RANGE_PCT = 0.35;

// MT4 color palette (BuyCandleColor=clrGreen, SellCandleColor=clrMaroon — same as base Shadow Breaker)
const BUY_COLOR = "#008000";
const SELL_COLOR = "#800000";
const NEUTRAL_COLOR = "#808080";
const TP_HIT_COLOR = "#32CD32";
const SL_TSL_COLOR = "#FF4500";

// ─── ATR (Wilder smoothing) ────────────────────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const len = candles.length;
  const atr: number[] = new Array(len).fill(0);
  if (len < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < len; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  if (period - 1 < len) atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── SuperTrend (single line, ratchet + flip on close) ─────────────
function computeSuperTrend(
  candles: CandleData[], period: number, multiplier: number, atr: number[],
): { direction: Direction[]; line: number[] } {
  const len = candles.length;
  const direction: Direction[] = new Array(len).fill(DIR_NONE);
  const line: number[] = new Array(len).fill(0);
  if (len < period + 2) return { direction, line };

  for (let i = period; i < len; i++) {
    const hl2 = (candles[i].high + candles[i].low) / 2;
    let ub = hl2 + multiplier * atr[i];
    let lb = hl2 - multiplier * atr[i];
    const close = candles[i].close;

    if (direction[i - 1] === DIR_NONE) {
      direction[i] = close >= hl2 ? DIR_LONG : DIR_SHORT;
      line[i] = direction[i] === DIR_LONG ? lb : ub;
      continue;
    }

    const pd = direction[i - 1];
    const pl = line[i - 1];
    if (pd === DIR_LONG) {
      if (lb < pl) lb = pl;
      if (close < lb) { direction[i] = DIR_SHORT; line[i] = ub; }
      else { direction[i] = DIR_LONG; line[i] = lb; }
    } else {
      if (ub > pl) ub = pl;
      if (close > ub) { direction[i] = DIR_LONG; line[i] = lb; }
      else { direction[i] = DIR_SHORT; line[i] = ub; }
    }
  }
  return { direction, line };
}

// ─── Nadaraya-Watson causal Gaussian envelope + band-touch signals ─
function computeNadarayaWatson(
  candles: CandleData[], bandwidth: number, multiplier: number,
): { buyTouch: boolean[]; sellTouch: boolean[] } {
  const len = candles.length;
  const buyTouch: boolean[] = new Array(len).fill(false);
  const sellTouch: boolean[] = new Array(len).fill(false);
  const kernel = Math.max(bandwidth * 5, 20);
  if (len < kernel + 2) return { buyTouch, sellTouch };

  const coefs: number[] = new Array(kernel);
  let den = 0;
  for (let k = 0; k < kernel; k++) {
    coefs[k] = Math.exp(-(k * k) / (bandwidth * bandwidth * 2.0));
    den += coefs[k];
  }
  if (den === 0) return { buyTouch, sellTouch };

  const closes = candles.map(c => c.close);
  const yArr: number[] = new Array(len).fill(NaN);
  for (let j = kernel - 1; j < len; j++) {
    let s = 0;
    for (let k = 0; k < kernel; k++) s += closes[j - k] * coefs[k];
    yArr[j] = s / den;
  }

  // Mean absolute error over closed-bar history (excludes the still-forming live bar)
  let sumE = 0, cnt = 0;
  for (let j = kernel - 1; j <= len - 2; j++) {
    if (!Number.isNaN(yArr[j])) { sumE += Math.abs(closes[j] - yArr[j]); cnt++; }
  }
  if (cnt === 0) return { buyTouch, sellTouch };
  const mae = (sumE / cnt) * multiplier;

  const upper: number[] = new Array(len).fill(NaN);
  const lower: number[] = new Array(len).fill(NaN);
  for (let j = kernel - 1; j < len; j++) {
    upper[j] = yArr[j] + mae;
    lower[j] = yArr[j] - mae;
  }

  for (let j = kernel; j < len; j++) {
    if (Number.isNaN(upper[j]) || Number.isNaN(upper[j - 1])) continue;
    if (closes[j] >= upper[j] && closes[j - 1] < upper[j - 1]) sellTouch[j] = true;
    if (closes[j] <= lower[j] && closes[j - 1] > lower[j - 1]) buyTouch[j] = true;
  }
  return { buyTouch, sellTouch };
}

// ─── Dashboard data interface ───────────────────────────────────────
export interface ShadowBreakerProDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Waiting" | "Active" | "Target Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  targetHit: boolean;
  slHit: boolean;
  tslHit: boolean;
  /** True when this entry immediately followed an opposite-direction SL/TSL exit. */
  isReversal: boolean;
  candleState: "long" | "short" | "neutral";
}

export const shadowBreakerProIndicator: IndicatorDefinition = {
  id: "shadow_breaker_pro",
  name: "Shadow Breaker Pro",
  shortName: "SBP",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "showTrailingStop", label: "Show Moving SL", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#FFFFFF", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF0000", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target", label: "Target", color: "#00C850", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const displayLevels = params.displayLevels ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const len = candles.length;
    const kernel = Math.max(ND_BANDWIDTH * 5, 20);
    const minBars = kernel + ST_PERIOD + 5;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] }, { key: "target", data: [] },
    ];
    if (len < minBars) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));

    // ═══════════════════════════════════════════════════════════════
    // 1. Nadaraya-Watson band-touch arming signals
    // ═══════════════════════════════════════════════════════════════
    const { buyTouch, sellTouch } = computeNadarayaWatson(candles, ND_BANDWIDTH, ND_MULTIPLIER);

    // ═══════════════════════════════════════════════════════════════
    // 2. SuperTrend (trigger)
    // ═══════════════════════════════════════════════════════════════
    const atr14 = computeATR(candles, ST_PERIOD);
    const { direction: stDir, line: stLine } = computeSuperTrend(candles, ST_PERIOD, ST_MULTIPLIER, atr14);
    const atrBufFor = (i: number) => (atr14[i] > 0 ? atr14[i] * 0.5 : tick * 10);

    // ═══════════════════════════════════════════════════════════════
    // 3. Signal state machine — arm on ND touch, trigger on ST flip
    // ═══════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number; type: "signal" | "tp" | "sl" | "tsl"; position: "above" | "below" }[] = [];
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let waitBuy = false, waitSell = false;
    let lastColorDir: Direction = DIR_NONE;
    // Tracks the direction of the last SL/TSL stop-out (not Target hits) until
    // the next opposite-direction entry consumes it — mirrors MT4's lastExitDir,
    // used purely to tag a fresh entry as a "reversal" re-entry.
    let lastExitDir: Direction = DIR_NONE;

    let sc_active = false;
    let sc_type: "BUY" | "SELL" | "" = "";
    let sc_entryBarIdx = -1;
    let sc_entry = 0, sc_sl = 0, sc_tsl = 0, sc_tp = 0;
    let sc_targetHit = false;
    let sc_isReversal = false;
    let sc_status: ShadowBreakerProDashboard["status"] = "Waiting";
    let sc_booked = 0;
    let sc_exitKind: ExitKind = "";
    let lastSignalBar = -1;

    const lastClosedBar = len - 2;

    // ─── MT4-style exit label text ────────────────────────────────
    // Matches ShadowBreaker_Pro_V2.mq4's SB_DrawExitLabel() format exactly:
    //   "TARGET HIT @ 8806.00  (+116.00 pts)"
    //   "TSL HIT @ 8754.00  (-109.00 pts)"
    //   "SL HIT @ 8501.00  (-189.00 pts)"
    const formatPts = (pts: number) => (pts >= 0 ? `+${pts.toFixed(2)}` : pts.toFixed(2));

    // Marker helpers only ever read/append to arrays — they never reassign
    // the sc_* state, so they stay outside the loop's own narrowing scope
    // without triggering TS's "always-widened" quirk for closured `let`s.
    const pushSignalMarker = (j: number, isBuy: boolean) => {
      const direction = isBuy ? "long" : "short";
      const price = isBuy ? candles[j].low : candles[j].high;
      arrowMarkers.push({ time: candles[j].time, direction, price, type: "signal", position: isBuy ? "below" : "above" });
      markers.push({ time: candles[j].time, position: isBuy ? "belowBar" : "aboveBar", color: isBuy ? BUY_COLOR : SELL_COLOR, shape: isBuy ? "arrowUp" : "arrowDown", text: "", size: 2 });
    };
    const pushTpMarker = (j: number, isBuy: boolean, exitPrice: number, bookedPts: number) => {
      const price = isBuy ? candles[j].high : candles[j].low;
      arrowMarkers.push({ time: candles[j].time, direction: isBuy ? "long" : "short", price, type: "tp", position: isBuy ? "above" : "below" });
      const text = `TARGET HIT @ ${exitPrice.toFixed(2)}  (${formatPts(bookedPts)} pts)`;
      markers.push({ time: candles[j].time, position: isBuy ? "aboveBar" : "belowBar", shape: "circle", color: TP_HIT_COLOR, text, size: 1 });
    };
    const pushExitMarker = (j: number, isBuy: boolean, kind: "sl" | "tsl", exitPrice: number, bookedPts: number) => {
      const price = isBuy ? candles[j].low : candles[j].high;
      arrowMarkers.push({ time: candles[j].time, direction: isBuy ? "long" : "short", price, type: kind, position: isBuy ? "below" : "above" });
      const text = `${kind === "tsl" ? "TSL" : "SL"} HIT @ ${exitPrice.toFixed(2)}  (${formatPts(bookedPts)} pts)`;
      markers.push({ time: candles[j].time, position: isBuy ? "belowBar" : "aboveBar", shape: "circle", color: SL_TSL_COLOR, text, size: 1 });
    };

    // All reads/writes of sc_type, sc_active, lastColorDir, etc. below stay
    // inline within this loop's own scope (rather than nested closures) so
    // TypeScript's control-flow narrowing can see every assignment.
    for (let j = 1; j <= lastClosedBar; j++) {
      // 1. Trailing stop ratchet (only while active)
      if (sc_active) {
        if (sc_type === "BUY" && stDir[j] === DIR_LONG) {
          const t = stLine[j] - atrBufFor(j) * TSL_ATR_BUFFER;
          if (t > sc_tsl) sc_tsl = t;
        } else if (sc_type === "SELL" && stDir[j] === DIR_SHORT) {
          const t = stLine[j] + atrBufFor(j) * TSL_ATR_BUFFER;
          if (t < sc_tsl) sc_tsl = t;
        }
      }

      // 2. Target / SL / TSL exit checks (bars after the entry bar).
      //    Target is the ONLY level: touching it closes the whole trade.
      if (sc_active && sc_entryBarIdx >= 0 && j > sc_entryBarIdx) {
        const isBuy = sc_type === "BUY";
        const effSL = isBuy ? Math.max(sc_sl, sc_tsl) : Math.min(sc_sl, sc_tsl);
        const hi = candles[j].high, lo = candles[j].low;
        const plSign = isBuy ? 1 : -1;

        const hitTarget = isBuy ? hi >= sc_tp : lo <= sc_tp;
        const hitSL = isBuy ? lo <= effSL : hi >= effSL;

        // Target has priority over SL/TSL when both are touched on the same bar
        if (hitTarget) {
          sc_targetHit = true; sc_active = false; sc_status = "Target Hit";
          sc_booked = plSign * (sc_tp - sc_entry);
          pushTpMarker(j, isBuy, sc_tp, sc_booked);
        } else if (hitSL) {
          sc_active = false;
          sc_exitKind = sc_tsl !== sc_sl && ((isBuy && sc_tsl > sc_sl) || (!isBuy && sc_tsl < sc_sl)) ? "tsl" : "sl";
          sc_status = sc_exitKind === "tsl" ? "Trailing SL Hit" : "Stop Loss Hit";
          sc_booked = plSign * (effSL - sc_entry);
          lastExitDir = isBuy ? DIR_LONG : DIR_SHORT;
          pushExitMarker(j, isBuy, sc_exitKind, effSL, sc_booked);
          lastColorDir = DIR_NONE; // neutral candles after SL/TSL exit
        }
      }

      // 3. ND arm flags — captured BEFORE this bar can arm (mirrors MT4 order)
      const prevWaitBuy = waitBuy, prevWaitSell = waitSell;
      if (buyTouch[j] && !(sc_active && sc_type === "BUY")) waitBuy = true;
      if (sellTouch[j] && !(sc_active && sc_type === "SELL")) waitSell = true;

      // 4. SuperTrend flip detection
      const stFlipUp = stDir[j - 1] === DIR_SHORT && stDir[j] === DIR_LONG;
      const stFlipDown = stDir[j - 1] === DIR_LONG && stDir[j] === DIR_SHORT;

      // 5. SuperTrend-flip-triggered TSL/SL exit against an active trade
      if (sc_active) {
        if (sc_type === "BUY" && stFlipDown) {
          const effSL = Math.max(sc_sl, sc_tsl);
          sc_active = false;
          sc_exitKind = sc_tsl > sc_sl ? "tsl" : "sl";
          sc_status = sc_exitKind === "tsl" ? "Trailing SL Hit" : "Stop Loss Hit";
          sc_booked = effSL - sc_entry;
          lastExitDir = DIR_LONG;
          pushExitMarker(j, true, sc_exitKind, effSL, sc_booked);
          lastColorDir = DIR_NONE;
          if (buyTouch[j]) waitBuy = true;
          if (sellTouch[j]) waitSell = true;
        } else if (sc_type === "SELL" && stFlipUp) {
          const effSL = Math.min(sc_sl, sc_tsl);
          sc_active = false;
          sc_exitKind = sc_tsl < sc_sl ? "tsl" : "sl";
          sc_status = sc_exitKind === "tsl" ? "Trailing SL Hit" : "Stop Loss Hit";
          sc_booked = sc_entry - effSL;
          lastExitDir = DIR_SHORT;
          pushExitMarker(j, false, sc_exitKind, effSL, sc_booked);
          lastColorDir = DIR_NONE;
          if (buyTouch[j]) waitBuy = true;
          if (sellTouch[j]) waitSell = true;
        }
      }

      // 6. Entry — only while flat, rejecting "thin" candles whose own
      //    High-Low range is too small relative to ATR to trust as a real
      //    breakout bar (common right at a V-reversal).
      if (!sc_active) {
        const candleRange = candles[j].high - candles[j].low;
        const isThinCandle = candleRange < atrBufFor(j) * THIN_CANDLE_MIN_RANGE_PCT;
        const canEnterBuy = stFlipUp && prevWaitBuy && !isThinCandle;
        const canEnterSell = stFlipDown && prevWaitSell && !isThinCandle;

        if (canEnterBuy) {
          const stVal = stLine[j];
          const entryPx = candles[j].close;
          const buffer = atrBufFor(j) * TSL_ATR_BUFFER;
          let risk = Math.abs(entryPx - (stVal - buffer));
          if (risk < tick * 2) risk = atrBufFor(j) * 2;
          sc_active = true; sc_entryBarIdx = j; sc_type = "BUY";
          sc_entry = roundToTick(entryPx, tick);
          sc_sl = roundToTick(sc_entry - SL_RR * risk, tick);
          sc_tsl = sc_sl;
          sc_tp = roundToTick(sc_entry + TP_RR * risk, tick);
          sc_targetHit = false;
          sc_isReversal = lastExitDir === DIR_SHORT;
          if (lastExitDir === DIR_SHORT) lastExitDir = DIR_NONE;
          sc_status = "Active"; sc_booked = 0; sc_exitKind = "";
          lastColorDir = DIR_LONG; lastSignalBar = j; waitBuy = false;
          pushSignalMarker(j, true);
        } else if (canEnterSell) {
          const stVal = stLine[j];
          const entryPx = candles[j].close;
          const buffer = atrBufFor(j) * TSL_ATR_BUFFER;
          let risk = Math.abs(entryPx - (stVal + buffer));
          if (risk < tick * 2) risk = atrBufFor(j) * 2;
          sc_active = true; sc_entryBarIdx = j; sc_type = "SELL";
          sc_entry = roundToTick(entryPx, tick);
          sc_sl = roundToTick(sc_entry + SL_RR * risk, tick);
          sc_tsl = sc_sl;
          sc_tp = roundToTick(sc_entry - TP_RR * risk, tick);
          sc_targetHit = false;
          sc_isReversal = lastExitDir === DIR_LONG;
          if (lastExitDir === DIR_LONG) lastExitDir = DIR_NONE;
          sc_status = "Active"; sc_booked = 0; sc_exitKind = "";
          lastColorDir = DIR_SHORT; lastSignalBar = j; waitSell = false;
          pushSignalMarker(j, false);
        }
      }

      candleColorState[j] = lastColorDir === DIR_LONG ? "long" : lastColorDir === DIR_SHORT ? "short" : "neutral";
    }

    // ═══════════════════════════════════════════════════════════════
    // 4. Live/forming bar — TSL ratchet + Target/SL hit checks only (no new
    //    entries, since the ST/ND state of a still-forming bar can repaint
    //    intrabar). Hits are booked for the dashboard but not marked on the
    //    chart until the bar closes, matching the other Premium Strategies.
    // ═══════════════════════════════════════════════════════════════
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      const j = len - 1;
      if (sc_active) {
        if (sc_type === "BUY" && stDir[j] === DIR_LONG) {
          const t = stLine[j] - atrBufFor(j) * TSL_ATR_BUFFER;
          if (t > sc_tsl) sc_tsl = t;
        } else if (sc_type === "SELL" && stDir[j] === DIR_SHORT) {
          const t = stLine[j] + atrBufFor(j) * TSL_ATR_BUFFER;
          if (t < sc_tsl) sc_tsl = t;
        }
      }
      if (sc_active && sc_entryBarIdx >= 0 && j > sc_entryBarIdx) {
        const isBuy = sc_type === "BUY";
        const effSL = isBuy ? Math.max(sc_sl, sc_tsl) : Math.min(sc_sl, sc_tsl);
        const hi = candles[j].high, lo = candles[j].low;
        const plSign = isBuy ? 1 : -1;

        const hitTarget = isBuy ? hi >= sc_tp : lo <= sc_tp;
        const hitSL = isBuy ? lo <= effSL : hi >= effSL;

        if (hitTarget) {
          sc_targetHit = true; sc_active = false; sc_status = "Target Hit";
          sc_booked = plSign * (sc_tp - sc_entry);
        } else if (hitSL) {
          sc_active = false;
          sc_exitKind = sc_tsl !== sc_sl && ((isBuy && sc_tsl > sc_sl) || (!isBuy && sc_tsl < sc_sl)) ? "tsl" : "sl";
          sc_status = sc_exitKind === "tsl" ? "Trailing SL Hit" : "Stop Loss Hit";
          sc_booked = plSign * (effSL - sc_entry);
          lastColorDir = DIR_NONE;
        }
      }
      candleColorState[j] = lastColorDir === DIR_LONG ? "long" : lastColorDir === DIR_SHORT ? "short" : "neutral";
    } else if (len > 0) {
      candleColorState[len - 1] = candleColorState[len - 2] ?? "neutral";
    }

    // ═══════════════════════════════════════════════════════════════
    // 5. Dashboard metadata
    // ═══════════════════════════════════════════════════════════════
    const currentPrice = candles[len - 1]?.close ?? 0;
    let dashSignal: ShadowBreakerProDashboard["signal"] = null;
    let dashTrend: ShadowBreakerProDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (sc_entry > 0) {
      dashSignal = sc_type === "BUY" ? "BUY" : "SELL";
      dashTrend = sc_type === "BUY" ? "LONG" : "SHORT";
      if (sc_active) livePL = sc_type === "BUY" ? currentPrice - sc_entry : sc_entry - currentPrice;
    }

    const dashboard: ShadowBreakerProDashboard = {
      signal: dashSignal, trend: dashTrend,
      entry: sc_entry > 0 ? sc_entry : null,
      stopLoss: sc_entry > 0 ? sc_sl : null,
      trailingStop: sc_entry > 0 ? sc_tsl : null,
      target: sc_entry > 0 ? sc_tp : null,
      currentPrice, livePL, bookedPL: sc_booked, status: sc_status,
      targetHit: sc_targetHit,
      slHit: sc_exitKind === "sl", tslHit: sc_exitKind === "tsl",
      isReversal: sc_isReversal,
      candleState: candleColorState[len - 1] ?? "neutral",
    };

    // ═══════════════════════════════════════════════════════════════
    // 6. Candle colors + price lines (plot series stay empty — priceLines
    //    handle all level drawing, matching the other Premium Strategies)
    // ═══════════════════════════════════════════════════════════════
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") {
      for (let i = 0; i < len; i++) {
        const s = candleColorState[i];
        candleColors.push({ time: candles[i].time, color: s === "long" ? BUY_COLOR : s === "short" ? SELL_COLOR : NEUTRAL_COLOR });
      }
    }

    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && sc_entry > 0 && lastSignalBar >= 0) {
      const fromTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "sbp-entry", price: sc_entry, color: "#FFFFFF", title: "Entry", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "sbp-sl", price: sc_sl, color: "#FF0000", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "sbp-tp", price: sc_tp, color: "#00C850", title: "Target", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
      );
      if (showTrailingStop !== "no" && sc_tsl > 0) {
        priceLines.push({ id: "sbp-tsl", price: sc_tsl, color: "#FFA500", title: "Moving SL", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime });
      }
    }

    return [
      { key: "entry", data: [], markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: [] },
      { key: "target", data: [] },
    ];
  },
};
